/**
 * BasePawnMovementComponent - abstract base for pawn locomotion. Defines movement
 * and look input interfaces. Subclasses implement physics-specific behavior.
 */
import * as THREE from 'three';

import { ClientRPC, ServerRPC } from '../../actors/ActorNetworking.js';
import { NetMovementPredictor } from '../../multiplayer/MovementPrediction.js';
import { AbstractEngineClass } from '../../systems/ClassRegistry.js';
import { property } from '../../utils/serialization/decorator.js';
import { SceneComponent } from '../SceneComponent.js';

import type { Pawn } from '../../actors/Pawn.js';

// ---------------------------------------------------------------------------
// Public enums and interfaces
// ---------------------------------------------------------------------------

/** Discrete movement state used for server validation and corrections. */
export enum MovementMode {
  Walking = 0,
  Falling = 1,
}

/**
 * A single predicted client move that can be replayed after a server correction.
 */
export interface SavedMove {
  sequence: number;
  deltaTime: number;
  /** Axis inputs for this move. */
  forward: number;
  right: number;
  lookRight: number;
  lookUp: number;
  zoom: number;
  isLookAbsolute: boolean;
  /** One-frame jump edge flags. */
  jumpPressed: boolean;
  jumpReleased: boolean;
  /** State immediately before this move was simulated (pre-jump). */
  startState: MovementStateSnapshot;
  /** State immediately after this move was simulated (post-physics). Null until finalised. */
  endState: MovementStateSnapshot | null;
}

/**
 * Input structure that can represent either absolute values or speed/rate values
 */
export class PawnInput {
  /** The input value */
  value: number = 0;

  /** True if the value represents an absolute amount, false if it represents a speed/rate */
  isAbsolute: boolean = false;

  constructor(value: number = 0, isAbsolute: boolean = false) {
    this.value = value;
    this.isAbsolute = isAbsolute;
  }
}

/**
 * Local input state for the pawn movement component
 */
export interface BasePawnMovementInputState {
  forwardInput: number;
  rightInput: number;
  lookUpInput: number;
  lookRightInput: number;
  zoomInput: number;
}

/**
 * Snapshot of all state required to fully restore a pawn's movement simulation.
 * Used by client-side prediction reconciliation to rewind and replay inputs.
 *
 * Base fields are transform-only; movement component subclasses extend this
 * with physics-specific state (velocities, grounded, etc.).
 */
export interface MovementStateSnapshot {
  position: THREE.Vector3;
  rotation: THREE.Euler;
  scale: THREE.Vector3;
  /** Discrete movement mode at the time of the snapshot. */
  movementMode?: MovementMode;
  /** Forward velocity (CharacterMovementComponent) */
  forwardVelocity?: number;
  /** Right/strafe velocity (CharacterMovementComponent) */
  rightVelocity?: number;
  /** Vertical velocity / gravity accumulation (CharacterMovementComponent) */
  verticalVelocity?: number;
  /** Whether the character was touching the ground */
  isGrounded?: boolean;
  /** Mid-air jumps consumed since last landing */
  jumpsUsed?: number;
}

// ---------------------------------------------------------------------------
// Exported server-side type
// ---------------------------------------------------------------------------

/**
 * A queued server-side input command representing one predicted move from the client.
 * Pawn.serverReceiveMovementInput (a @ServerRPC) constructs this and delegates to
 * BasePawnMovementComponent.serverReceiveMove.
 */
export interface ServerInputCommand {
  sequence: number;
  /** The deltaTime the client used to simulate this move (derived from timestamps). */
  deltaTime: number;
  forward: number;
  right: number;
  lookRight: number;
  lookUp: number;
  zoom: number;
  isLookAbsolute: boolean;
  jumpPressed: boolean;
  jumpReleased: boolean;
  /** Client-reported world position after simulating this move. */
  reportedEndPosition: THREE.Vector3;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Base class for all pawn movement components
 * Provides abstract methods for handling movement input
 */
@AbstractEngineClass('BasePawnMovementComponent')
export abstract class BasePawnMovementComponent extends SceneComponent {
  @property()
  public speedModifier: number = 1;

  @property()
  public jumpStrengthModifier: number = 1;

  // ---------------------------------------------------------------------------
  // Multiplayer configuration
  // ---------------------------------------------------------------------------

  /** Distance (world units) the client must diverge from server before a correction is issued. */
  @property({ category: 'Multiplayer' })
  public positionCorrectionThreshold: number = 0.1;

  /**
   * Minimum number of processed input sequences between consecutive corrections.
   * Prevents flooding the client with correction RPCs on persistent small errors.
   */
  @property({ category: 'Multiplayer' })
  public correctionCooldownInputs: number = 10;

  // ---------------------------------------------------------------------------
  // Replicated ack sequence (server → client)
  // Replaces Pawn.replicatedInputSequence.
  // ---------------------------------------------------------------------------

  /**
   * Last input sequence the server successfully processed.
   * Replicated to the owning client so it can prune its SavedMove queue
   * even when no position correction is needed.
   */
  @property({ replicate: true, transient: true, hidden: true, net: { netType: 'int', replicateTo: 'owner' } })
  protected replicatedAckSequence: number = -1;

  // ---------------------------------------------------------------------------
  // Raw inputs
  // ---------------------------------------------------------------------------

  protected forwardInput: PawnInput = new PawnInput();
  protected rightInput: PawnInput = new PawnInput();
  protected lookUpInput: PawnInput = new PawnInput();
  protected lookRightInput: PawnInput = new PawnInput();
  protected zoomInput: PawnInput = new PawnInput();

  // ---------------------------------------------------------------------------
  // Client-side prediction state (autonomous proxy only)
  // ---------------------------------------------------------------------------

  private clientSavedMoves: SavedMove[] = [];
  private pendingCorrection: { sequence: number; state: MovementStateSnapshot } | null = null;

  /** Set by beginLocalMove(), used by finalizeLocalMove(). */
  private pendingMoveStartState: MovementStateSnapshot | null = null;
  private pendingMoveDeltaTime: number = 0;

  /**
   * One-frame jump edge flags stored by PlayerController via setJumpEdges().
   * Applied in tickPrePhysics after startState is captured.
   */
  private pendingJumpPressed: boolean = false;
  private pendingJumpReleased: boolean = false;

  /** Monotonically-increasing sequence counter for outgoing moves. */
  private nextMoveSequence: number = 0;

  private static readonly CLIENT_SAVED_MOVE_MAX = 128;

  // ---------------------------------------------------------------------------
  // Server-side state (authority + owning client)
  // ---------------------------------------------------------------------------

  private serverInputQueue: ServerInputCommand[] = [];
  private lastProcessedSequence: number = -1;
  private lastCorrectionSentSequence: number = -1;
  private lastClientReportedEndPos: THREE.Vector3 | null = null;
  /** Set when serverDrainInputs processes at least one input so post-physics can finalize. */
  private serverSimulatedInputThisFrame: boolean = false;

  private static readonly SERVER_INPUT_QUEUE_MAX = 16;
  private static readonly SERVER_MAX_INPUTS_PER_TICK = 3;

  // ---------------------------------------------------------------------------
  // Input API
  // ---------------------------------------------------------------------------

  public getInputState(): BasePawnMovementInputState {
    return {
      forwardInput: this.forwardInput.value,
      rightInput: this.rightInput.value,
      lookUpInput: this.lookUpInput.value,
      lookRightInput: this.lookRightInput.value,
      zoomInput: this.zoomInput.value,
    };
  }

  public addForwardInput(value: number, isAbsolute: boolean = false): void {
    this.forwardInput.value = value;
    this.forwardInput.isAbsolute = isAbsolute;
  }

  public addRightInput(value: number, isAbsolute: boolean = false): void {
    this.rightInput.value = value;
    this.rightInput.isAbsolute = isAbsolute;
  }

  public addLookUpInput(value: number, isAbsolute: boolean = false): void {
    this.lookUpInput.value = value;
    this.lookUpInput.isAbsolute = isAbsolute;
  }

  public addLookRightInput(value: number, isAbsolute: boolean = false): void {
    this.lookRightInput.value = value;
    this.lookRightInput.isAbsolute = isAbsolute;
  }

  public addZoomInput(value: number, isAbsolute: boolean = false): void {
    this.zoomInput.value = value;
    this.zoomInput.isAbsolute = isAbsolute;
  }

  /**
   * Store per-frame jump edge flags from PlayerController.
   * These are consumed by tickPrePhysics after startState is captured.
   */
  public setJumpEdges(jumpPressed: boolean, jumpReleased: boolean): void {
    this.pendingJumpPressed = jumpPressed;
    this.pendingJumpReleased = jumpReleased;
  }

  public setPawnWorldTransform(transform: { position?: THREE.Vector3, rotation?: THREE.Euler, scale?: THREE.Vector3 }): void {
    const owner = this.getActor()!;
    const rootComponent = owner.rootComponent;
    if (transform.position) {
      rootComponent.position.copy(transform.position);
    }
    if (transform.rotation) {
      rootComponent.rotation.copy(transform.rotation);
    }
    if (transform.scale) {
      rootComponent.scale.copy(transform.scale);
    }
  }

  public jump(strength: number = 1): void { }

  public stopJump(): void { }

  /**
   * Handles mouse wheel events from the input system.
   * Override in subclasses to implement scroll-based behavior (e.g., zoom, speed adjustment).
   * @param e - The wheel event
   * @returns True if the event was handled, false otherwise
   */
  public handleMouseWheel(e: WheelEvent): boolean {
    return false;
  }

  // ---------------------------------------------------------------------------
  // Movement simulation — override in subclasses
  // ---------------------------------------------------------------------------

  /**
   * Returns the current discrete movement mode (Walking / Falling).
   * Override in subclasses that track movement mode.
   */
  protected getMovementMode(): MovementMode {
    return MovementMode.Walking;
  }

  /** Sets the movement mode. Override in subclasses that track movement mode. */
  protected setMovementMode(_mode: MovementMode): void { }

  /**
   * Perform one authoritative movement step using the full physics pipeline.
   * This is called:
   *   - Each normal tick for the local player / server authority
   *   - By the server when draining the client input queue (with client's deltaTime)
   *   - During correction replay for each unacknowledged saved move
   *
   * @param immediateBodySync - When true, the physics body position is also updated
   *   immediately so subsequent KCC sweeps in the same tick start from the correct
   *   position. Use for all non-final steps in a multi-input batch. See
   *   CharacterMovementComponent.performMovementStep for details.
   *
   * Base implementation is a no-op. Override in CharacterMovementComponent.
   */
  protected performMovementStep(_deltaTime: number, _immediateBodySync: boolean = false): void { }

  // ---------------------------------------------------------------------------
  // Snapshot capture / restore
  // ---------------------------------------------------------------------------

  /**
   * Capture the full movement state needed to restore and replay simulation.
   * Base implementation captures the owner's world transform only.
   * Override in subclasses to include physics-specific state (velocities, etc.).
   */
  public captureMovementState(): MovementStateSnapshot {
    const owner = this.getActor()!;
    const root = owner.rootComponent;
    return {
      position: root.position.clone(),
      rotation: root.rotation.clone(),
      scale: root.scale.clone(),
      movementMode: this.getMovementMode(),
    };
  }

  /**
   * Restore movement state from a snapshot produced by {@link captureMovementState}.
   * Base implementation restores the owner's world transform only.
   * Override in subclasses to restore physics-specific state.
   */
  public restoreMovementState(snapshot: MovementStateSnapshot): void {
    const owner = this.getActor()!;
    const root = owner.rootComponent;
    root.position.copy(snapshot.position);
    root.rotation.copy(snapshot.rotation);
    root.scale.copy(snapshot.scale);
    if (snapshot.movementMode !== undefined) {
      this.setMovementMode(snapshot.movementMode);
    }
  }

  // ---------------------------------------------------------------------------
  // Prediction API
  // ---------------------------------------------------------------------------

  /**
   * SERVER RPC: client sends its sequenced input + deltaTime + end position every frame.
   * Enqueues the command for processing in the next server tick.
   */
  @ServerRPC({
    reliable: false,
    params: [
      { netType: 'uint' },                                                 // sequence
      { netType: 'float', quantization: { mode: 'scale', scale: 1000 } },  // deltaTime
      { netType: 'float', quantization: { mode: 'scale', scale: 100 } },   // forward
      { netType: 'float', quantization: { mode: 'scale', scale: 100 } },   // right
      { netType: 'float', quantization: { mode: 'scale', scale: 100 } },   // lookUp
      { netType: 'float', quantization: { mode: 'scale', scale: 100 } },   // lookRight
      { netType: 'float', quantization: { mode: 'scale', scale: 100 } },   // zoom
      null,                                                                // isLookAbsolute
      null,                                                                // jumpPressed
      null,                                                                // jumpReleased
      { netType: 'float', quantization: { mode: 'scale', scale: 1000 } },  // endPosX
      { netType: 'float', quantization: { mode: 'scale', scale: 1000 } },  // endPosY
      { netType: 'float', quantization: { mode: 'scale', scale: 1000 } },  // endPosZ
    ]
  })
  public serverReceiveMovementInput(
    sequence: number,
    deltaTime: number,
    forward: number,
    right: number,
    lookUp: number,
    lookRight: number,
    zoom: number,
    isLookAbsolute: boolean,
    jumpPressed: boolean = false,
    jumpReleased: boolean = false,
    endPosX: number = 0,
    endPosY: number = 0,
    endPosZ: number = 0,
  ): void {
    this.serverReceiveMove({
      sequence,
      deltaTime,
      forward,
      right,
      lookUp,
      lookRight,
      zoom,
      isLookAbsolute,
      jumpPressed,
      jumpReleased,
      reportedEndPosition: new THREE.Vector3(endPosX, endPosY, endPosZ),
    });
  }

  /**
   * CLIENT RPC: server sends an authoritative correction when divergence is detected.
   * Snaps to the corrected state and schedules a replay of unacknowledged moves.
   */
  @ClientRPC({
    params: [
      { netType: 'uint' },                                                           // sequence
      { netType: 'float', quantization: { mode: 'scale', scale: 1000 } },           // posX
      { netType: 'float', quantization: { mode: 'scale', scale: 1000 } },           // posY
      { netType: 'float', quantization: { mode: 'scale', scale: 1000 } },           // posZ
      { netType: 'float', quantization: { mode: 'range', min: -3.141592653589793, max: 3.141592653589793, bits: 16 } }, // rotX
      { netType: 'float', quantization: { mode: 'range', min: -3.141592653589793, max: 3.141592653589793, bits: 16 } }, // rotY
      { netType: 'float', quantization: { mode: 'range', min: -3.141592653589793, max: 3.141592653589793, bits: 16 } }, // rotZ
      { netType: 'float', quantization: { mode: 'scale', scale: 100 } },            // forwardVelocity
      { netType: 'float', quantization: { mode: 'scale', scale: 100 } },            // rightVelocity
      { netType: 'float', quantization: { mode: 'scale', scale: 100 } },            // verticalVelocity
      { netType: 'uint' },                                                           // movementMode
      { netType: 'uint' },                                                           // jumpsUsed
    ]
  })
  public clientCorrectMovement(
    sequence: number,
    posX: number,
    posY: number,
    posZ: number,
    rotX: number,
    rotY: number,
    rotZ: number,
    forwardVelocity: number,
    rightVelocity: number,
    verticalVelocity: number,
    movementMode: number,
    jumpsUsed: number,
  ): void {
    if (!this.isLocalAutonomousProxy()) return;

    const snapshot: MovementStateSnapshot = {
      position: new THREE.Vector3(posX, posY, posZ),
      rotation: new THREE.Euler(rotX, rotY, rotZ),
      scale: this.getActor()!.rootComponent.scale.clone(),
      forwardVelocity,
      rightVelocity,
      verticalVelocity,
      movementMode,
      jumpsUsed,
    };

    this.applyServerCorrection({ sequence, state: snapshot });
  }

  /**
   * SERVER: Enqueue a client move command.
   */
  public serverReceiveMove(cmd: ServerInputCommand): void {
    if (!this.getActor()?.hasAuthority()) return;
    if (cmd.sequence <= this.lastProcessedSequence) return;

    this.serverInputQueue.push(cmd);
    // Keep sorted by sequence (usually arrive in order, but sort for safety)
    this.serverInputQueue.sort((a, b) => a.sequence - b.sequence);
    if (this.serverInputQueue.length > BasePawnMovementComponent.SERVER_INPUT_QUEUE_MAX) {
      console.warn(`[BasePawnMovementComponent] Server input queue overflow (${this.serverInputQueue.length} inputs). Dropping oldest — client may be sending too fast or server is severely behind.`);
      while (this.serverInputQueue.length > BasePawnMovementComponent.SERVER_INPUT_QUEUE_MAX) {
        this.serverInputQueue.shift();
      }
    }
  }

  /**
   * CLIENT: Apply a server correction.
   * Called by Pawn.clientCorrectMovement (the @ClientRPC stub).
   * Defers the actual reconciliation to the next tickPrePhysics for deterministic ordering.
   */
  public applyServerCorrection(correction: { sequence: number; state: MovementStateSnapshot }): void {
    if (!this.getActor()?.isLocalAutonomousProxy()) return;
    this.pendingCorrection = correction;
  }

  // ---------------------------------------------------------------------------
  // Internal: begin / finalise local move (autonomous proxy)
  // ---------------------------------------------------------------------------

  /**
   * Begin recording a new local move.
   * Captures the pre-jump start state. Must be called after any pending
   * correction has been applied (see tickPrePhysics) and before jump / inputs
   * are applied for this frame.
   */
  private beginLocalMove(deltaTime: number): void {
    this.pendingMoveStartState = this.captureMovementState();
    this.pendingMoveDeltaTime = deltaTime;
  }

  /**
   * Finalise the current local move after physics has run.
   * Captures end state, pushes to the SavedMove queue, and sends the move RPC.
   */
  private finalizeLocalMove(): void {
    const owner = this.getActor() as Pawn | null;
    if (!owner?.isLocalAutonomousProxy()) return;
    if (!this.pendingMoveStartState) return;

    const endState = this.captureMovementState();
    const sequence = this.nextMoveSequence++;

    const savedMove: SavedMove = {
      sequence,
      deltaTime: this.pendingMoveDeltaTime,
      forward: this.forwardInput.value,
      right: this.rightInput.value,
      lookRight: this.lookRightInput.value,
      lookUp: this.lookUpInput.value,
      zoom: this.zoomInput.value,
      isLookAbsolute: this.lookRightInput.isAbsolute,
      jumpPressed: this.pendingJumpPressed,
      jumpReleased: this.pendingJumpReleased,
      startState: this.pendingMoveStartState,
      endState,
    };

    this.clientSavedMoves.push(savedMove);
    while (this.clientSavedMoves.length > BasePawnMovementComponent.CLIENT_SAVED_MOVE_MAX) {
      this.clientSavedMoves.shift();
    }

    this.pendingMoveStartState = null;
    // Jump edge flags consumed — cleared for next frame
    this.pendingJumpPressed = false;
    this.pendingJumpReleased = false;

    this.serverReceiveMovementInput(
      sequence,
      savedMove.deltaTime,
      savedMove.forward,
      savedMove.right,
      savedMove.lookUp,
      savedMove.lookRight,
      savedMove.zoom,
      savedMove.isLookAbsolute,
      savedMove.jumpPressed,
      savedMove.jumpReleased,
      endState.position.x,
      endState.position.y,
      endState.position.z,
    );
  }

  /** Prune SavedMoves that the server has already acknowledged. */
  private pruneAckedMoves(): void {
    if (this.replicatedAckSequence < 0) return;
    this.clientSavedMoves = this.clientSavedMoves.filter(m => m.sequence > this.replicatedAckSequence);
  }

  /** onRep callback — fires when replicatedAckSequence changes on the client. */
  public onRepReplicatedAckSequence(): void {
    const owner = this.getActor() as Pawn | null;
    if (!owner?.isLocalAutonomousProxy()) return;
    this.pruneAckedMoves();
  }

  // ---------------------------------------------------------------------------
  // Internal: server drain
  // ---------------------------------------------------------------------------

  /**
   * Drain up to {@link SERVER_MAX_INPUTS_PER_TICK} queued client inputs this tick.
   *
   * Each input is simulated with the client's own deltaTime so the velocity
   * state (gravity, jump, acceleration) evolves correctly across all sub-steps.
   * Intermediate inputs use immediateBodySync=true so each KCC sweep starts from
   * the position produced by the previous step. The final input uses false so
   * Rapier can compute the correct apparent velocity for applyImpulsesToDynamicBodies.
   *
   * Returns true if at least one input was processed.
   */
  private serverDrainInputs(): boolean {
    const owner = this.getActor() as Pawn | null;
    if (!owner) return false;
    if (this.serverInputQueue.length === 0) return false;

    let count = this.serverInputQueue.length;
    if (count > BasePawnMovementComponent.SERVER_MAX_INPUTS_PER_TICK) {
      console.warn(`[BasePawnMovementComponent] Server input queue has ${count} pending inputs (max ${BasePawnMovementComponent.SERVER_MAX_INPUTS_PER_TICK} per tick). Server may be falling behind.`);
      count = BasePawnMovementComponent.SERVER_MAX_INPUTS_PER_TICK;
    }

    for (let i = 0; i < count; i++) {
      const isLastInput = i === count - 1;
      this.serverSimulateOneInput(owner, !isLastInput);
    }
    return true;
  }

  /** Apply a single queued client input and advance movement. */
  private serverSimulateOneInput(owner: Pawn, immediateBodySync: boolean = false): void {
    const cmd = this.serverInputQueue.shift()!;
    this.lastProcessedSequence = cmd.sequence;
    this.lastClientReportedEndPos = cmd.reportedEndPosition;

    // Apply inputs from client command
    if (cmd.jumpReleased) this.stopJump();
    if (cmd.jumpPressed) this.jump();
    this.addForwardInput(cmd.forward, false);
    this.addRightInput(cmd.right, false);
    this.addLookRightInput(cmd.lookRight, cmd.isLookAbsolute);
    this.addLookUpInput(cmd.lookUp, cmd.isLookAbsolute);
    this.addZoomInput(cmd.zoom, false);

    // Simulate with CLIENT's deltaTime so the server mirrors the client's step size
    this.performMovementStep(cmd.deltaTime, immediateBodySync);
  }

  /** After physics sync, compare against the client's reported position and ack. */
  private serverFinalizeProcessedInput(): void {
    const owner = this.getActor() as Pawn | null;
    if (!owner) return;

    this.checkDivergenceAndCorrect(owner);

    // Replicate ack back so client can prune its SavedMove queue
    this.replicatedAckSequence = this.lastProcessedSequence;
  }

  private checkDivergenceAndCorrect(owner: Pawn): void {
    if (!this.lastClientReportedEndPos) return;

    const serverPos = owner.rootComponent.position;
    const distance = serverPos.distanceTo(this.lastClientReportedEndPos);
    const cooldownOk = this.lastCorrectionSentSequence < 0 || this.lastProcessedSequence - this.lastCorrectionSentSequence > this.correctionCooldownInputs;

    if (distance > this.positionCorrectionThreshold && cooldownOk) {
      this.lastCorrectionSentSequence = this.lastProcessedSequence;
      const snapshot = this.captureMovementState();
      const serverRot = owner.rootComponent.rotation;

      this.clientCorrectMovement(
        this.lastProcessedSequence,
        serverPos.x, serverPos.y, serverPos.z,
        serverRot.x, serverRot.y, serverRot.z,
        snapshot.forwardVelocity ?? 0,
        snapshot.rightVelocity ?? 0,
        snapshot.verticalVelocity ?? 0,
        this.getMovementMode(),
        snapshot.jumpsUsed ?? 0,
      );
    }
  }

  /**
   * True when this authority pawn should simulate movement from queued client RPCs
   * (dedicated/listen server with an active game session). Standalone assigns
   * netOwningClientId but has no session — those pawns use performMovementStep locally.
   */
  private shouldProcessServerInputQueue(owner: Pawn): boolean {
    if (!owner.hasAuthority()) {
      return false;
    }
    if (!this.netOwningClientId) {
      return false;
    }
    return !!owner.getWorld()?.netWorld?.getGameSession();
  }

  // ---------------------------------------------------------------------------
  // Tick
  // ---------------------------------------------------------------------------

  public override tickPrePhysics(deltaTime: number): void {
    super.tickPrePhysics(deltaTime);

    const owner = this.getActor() as Pawn | null;
    if (!owner) return;

    if (owner.isSimulatedProxy()) {
      // ----------------------------------------------------------------
      // Simulated proxies: smooth toward the latest replicated transform.
      // ----------------------------------------------------------------
      const actorPredictor = owner.getMovementPredictor();
      if (!actorPredictor) return;

      const rootComponent = owner.rootComponent;
      const predictedTransform = actorPredictor.predictCurrentTransform(true);
      if (predictedTransform) {
        const lerpedTransform = NetMovementPredictor.lerpTransform(
          {
            position: rootComponent.position,
            rotation: rootComponent.quaternion,
            scale: rootComponent.scale,
          },
          predictedTransform,
          NetMovementPredictor.LerpAlpha,
        );
        this.setPawnWorldTransform({
          position: lerpedTransform.position,
          rotation: new THREE.Euler().setFromQuaternion(lerpedTransform.rotation),
          scale: lerpedTransform.scale,
        });
      }
      return;
    }

    if (owner.isLocalAutonomousProxy()) {
      // ----------------------------------------------------------------
      // Autonomous proxy: apply any pending server correction, then
      // capture the start state for the new move.
      // ----------------------------------------------------------------
      if (this.pendingCorrection) {
        const correction = this.pendingCorrection;
        this.pendingCorrection = null;

        // Prune moves up to and including the corrected sequence
        const idx = this.clientSavedMoves.findIndex(m => m.sequence === correction.sequence);
        if (idx >= 0) {
          this.clientSavedMoves.splice(0, idx + 1);
        }

        // Snap to authoritative state. Use direct position write (not setPawnWorldTransform)
        // so that rootComponent.position is correct as the replay starting point.
        this.restoreMovementState(correction.state);

        // Replay all remaining unacknowledged moves with full collision resolution.
        // Intermediate moves use immediateBodySync=true so each KCC sweep starts from
        // the position produced by the previous step.
        const movesToReplay = this.clientSavedMoves;
        if (movesToReplay.length > 0) {
          console.debug(`[BasePawnMovementComponent] Replaying ${movesToReplay.length} moves after correction seq=${correction.sequence}`);
        }

        for (let i = 0; i < movesToReplay.length; i++) {
          const move = movesToReplay[i];
          const isLastMove = i === movesToReplay.length - 1;
          if (move.jumpReleased) this.stopJump();
          if (move.jumpPressed) this.jump();
          this.addForwardInput(move.forward, false);
          this.addRightInput(move.right, false);
          this.addLookRightInput(move.lookRight, move.isLookAbsolute);
          this.addLookUpInput(move.lookUp, move.isLookAbsolute);
          this.addZoomInput(move.zoom, false);
          this.performMovementStep(move.deltaTime, !isLastMove);
          move.endState = this.captureMovementState();
        }
      }

      // Capture start state (after correction, before this frame's jump)
      this.beginLocalMove(deltaTime);

      // Apply this frame's jump edges (stored by setJumpEdges from PlayerController)
      if (this.pendingJumpPressed) this.jump();
      if (this.pendingJumpReleased) this.stopJump();

      // Run movement before physics. Position is applied directly to rootComponent
      // inside performMovementStep; no post-physics sync is needed.
      this.performMovementStep(deltaTime);
      return;
    }

    if (this.shouldProcessServerInputQueue(owner)) {
      this.serverSimulatedInputThisFrame = this.serverDrainInputs();
      if (!this.serverSimulatedInputThisFrame) {
        // No client inputs arrived this tick. Run a zero-input step so gravity
        // and deceleration are applied and the kinematic body gets a fresh
        // setNextKinematicTranslation, keeping it settled on the ground.
        this.addForwardInput(0);
        this.addRightInput(0);
        this.addLookRightInput(0);
        this.addLookUpInput(0);
        this.addZoomInput(0);
        this.performMovementStep(deltaTime);
      }
    } else if (owner.hasAuthority()) {
      // Local authority: standalone, editor play, or authority pawn without networked input RPCs
      this.performMovementStep(deltaTime);
    }
  }

  public override tickPostPhysics(deltaTime: number): void {
    super.tickPostPhysics(deltaTime);

    const owner = this.getActor() as Pawn | null;
    if (!owner) return;

    if (owner.isSimulatedProxy()) return;

    if (this.shouldProcessServerInputQueue(owner)) {
      // Compare/correct using the post-physics rootComponent.position.
      if (this.serverSimulatedInputThisFrame) {
        this.serverFinalizeProcessedInput();
        this.serverSimulatedInputThisFrame = false;
      }
    } else if (owner.isLocalAutonomousProxy()) {
      // Movement ran in tickPrePhysics; capture end state and send the RPC.
      this.finalizeLocalMove();
    }

    // Track local transform for predictor (used by simulated proxy smoothing on other clients)
    const actorPredictor = owner.getMovementPredictor();
    if (actorPredictor) {
      const rootComponent = owner.rootComponent;
      actorPredictor.addLocalTransform({
        timestamp: NetMovementPredictor.currentTime(),
        position: rootComponent.position.clone(),
        rotation: rootComponent.quaternion.clone(),
        scale: rootComponent.scale.clone(),
      });
    }
  }

  //====================================================================================================
  // Editor Functions
  //====================================================================================================

  public override getEditorClassIcon(): string | null {
    return 'Icon_Path';
  }
}
